/**
 * @file use-delete-confirm.ts
 *
 * Hook for the "ask, then confirm a destructive delete" flow shared by list
 * screens: it tracks the pending target, the in-flight flag, and the error
 * message, and drives a {@link ConfirmModal}. `confirm` runs the supplied async
 * delete; on success it clears the target (closing the dialog), and on failure it
 * surfaces `errorMessage` without closing so the user can retry. Extracted so the
 * shipping sections (and future lists) don't each re-implement the same state.
 */

import { useState } from 'react';
import { notifyError, notifySuccess } from '../feedback/toast';

/** Optional localized toast messages fired on the delete outcome. */
export interface DeleteConfirmToasts {
  /** Shown on a successful delete. */
  success: string;
  /** Shown on a failed delete (alongside the inline dialog error). */
  error: string;
}

/** The delete-confirm state and controls for one list. */
export interface UseDeleteConfirmResult<T> {
  /** The item pending deletion, or `null` when no dialog is open. */
  target: T | null;
  /** Whether the delete request is in flight. */
  deleting: boolean;
  /** The failure message to show in the dialog, or `null`. */
  error: string | null;
  /** Opens the confirm dialog for `item`. */
  ask: (item: T) => void;
  /** Closes the dialog without deleting. */
  cancel: () => void;
  /** Runs the delete for the current target. */
  confirm: () => void;
}

/**
 * Manages the confirm-delete lifecycle for a list.
 *
 * @param remove - Performs the delete; resolves `true` on success.
 * @param errorMessage - Localized message shown in the dialog when the delete fails.
 * @param toasts - Optional success/error toast messages fired on the outcome.
 *
 * @returns The pending target, the loading/error state, and ask/cancel/confirm.
 */
export function useDeleteConfirm<T>(
  remove: (item: T) => Promise<boolean>,
  errorMessage: string,
  toasts?: DeleteConfirmToasts,
): UseDeleteConfirmResult<T> {
  const [target, setTarget] = useState<T | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ask = (item: T): void => {
    setError(null);
    setTarget(item);
  };

  const cancel = (): void => {
    setTarget(null);
  };

  const confirm = (): void => {
    if (!target) {
      return;
    }
    setDeleting(true);
    setError(null);
    void remove(target).then((ok) => {
      setDeleting(false);
      if (ok) {
        setTarget(null);
        if (toasts) {
          notifySuccess(toasts.success);
        }
      } else {
        setError(errorMessage);
        if (toasts) {
          notifyError(toasts.error);
        }
      }
    });
  };

  return { target, deleting, error, ask, cancel, confirm };
}
